import React, { FC } from 'react';
import { useController, useFormContext } from 'react-hook-form';

import { CreateTransfer } from '../Add';
import { type Action } from '~/lib/authz/eval';

import { AccountsSequence } from './AccountsSequence';

/** CREDIT: your bank account → counterparty. DEBIT: counterparty → your bank account. */
export const AchAccountsSequence: FC = () => {
  const { control, watch } = useFormContext<CreateTransfer>();
  const type = watch('ach.type') as 'DEBIT' | 'CREDIT' | undefined;
  const amount = watch('ach.amount') as number | undefined;

  const bank = useController({
    control,
    name: 'ach.bankAccountId',
    rules: { required: 'Please fill out the Bank Account field' },
    defaultValue: '',
  });
  const counterparty = useController({
    control,
    name: 'ach.counterpartyId',
    rules: { required: 'Please fill out the Counterparty field' },
    defaultValue: '',
  });

  const isDebit = type === 'DEBIT';
  const permission: Action['permission'] | undefined =
    type === 'DEBIT' ? 'achDebits' : type === 'CREDIT' ? 'achCredits' : undefined;
  const unauthorizedText = `You do not have permission to use ${isDebit ? 'ACH debits' : 'ACH credits'} on this bank account.`;

  const bankDescription = isDebit
    ? 'Select the bank account the funds will be deposited into.'
    : 'Select the bank account the funds will be sent from.';
  const counterpartyDescription = isDebit
    ? 'Select or create the counterparty the funds will be pulled from.'
    : 'Select or create the counterparty that will receive the funds.';

  return (
    <AccountsSequence
      originatesFrom={isDebit ? 'counterparty' : 'bankAccount'}
      bankAccount={{
        label: 'Bank Account',
        description: bankDescription,
        value: (bank.field.value as string) || '',
        onChange: bank.field.onChange,
        onBlur: bank.field.onBlur,
        hasError: Boolean(bank.fieldState.error),
        permission,
        unauthorizedText,
        amount,
        amountCurrency: 'USD',
        dataCy: 'form-field-ach.bankAccountId',
      }}
      counterparty={{
        label: 'Counterparty',
        description: counterpartyDescription,
        value: (counterparty.field.value as string) || '',
        onChange: counterparty.field.onChange,
        onBlur: counterparty.field.onBlur,
        hasError: Boolean(counterparty.fieldState.error),
        counterpartyType: 'ach',
        dataCy: 'form-field-ach.counterpartyId',
      }}
    />
  );
};

export default AchAccountsSequence;
